import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useRouter } from '@tanstack/react-router'
import { ArrowLeft, Eye } from 'lucide-react'
import { useRef } from 'react'

import { DASHBOARD } from '@brickup/shared/locales/de/dashboard'
import { PROJECTS } from '@brickup/shared/locales/de/projects'
import { customerProjectQuery } from '@/api/configurator'
import { useUsersApi } from '@/api/users'
import { Callout, LightThemeScope } from '@/components/base'
import { CustomerShell } from '@/components/Customer/Shell'
import { useRouteProjectId } from '@/contexts/customer-project-context'
import { ShadowUserProvider } from '@/contexts/shadow-context'
import { useBareChrome } from '@/hooks/use-bare-chrome'
import { useCanChangeProject } from '@/hooks/use-project-access'
import { asRoute } from '@/utils/runtime-route'

/** Where "leave" lands when the advisor arrived without a `from`. */
const FALLBACK_EXIT = '/internal/projects'

type ShadowSearch = {
  /** Where the advisor came from, so leaving returns them there. */
  from?: string
}

/**
 * The advisor's "as customer" shell.
 *
 * It renders the customer-facing screens in the customer's own shell, so what
 * the advisor sees is what the customer sees; the banner is the only thing on
 * top. `ShadowUserProvider` is what makes every query, route and mutation
 * underneath target that customer rather than the signed-in advisor.
 */
function CustomerViewLayout() {
  const { userId } = Route.useParams()
  const { from } = Route.useSearch()
  const router = useRouter()

  const id = Number(userId)
  const isBare = useBareChrome()

  // Held across the navigations that drop the search param — captured on the
  // first render that carries it.
  const entryUrl = useRef<string | null>(null)
  if (entryUrl.current === null && from) entryUrl.current = from

  // The notice below is about writing one project, so it needs the project the
  // reader has open — the shell also spans the dashboard and the matches,
  // where there is none and nothing is writable anyway.
  const openProject = useRouteProjectId()
  const { data: shadowProject } = useQuery(
    customerProjectQuery({ shadowUserId: id, projectId: openProject }),
  )
  const canChange = useCanChangeProject(shadowProject)
  // Only once a project is open and loaded: the notice is a statement about
  // writing that project, and a screen with none — the dashboard, the
  // matches — would otherwise claim another advisor is responsible for
  // nothing.
  const watchingOnly = shadowProject != null && !canChange

  const { useGetQuery } = useUsersApi()
  const { data: targetUser } = useGetQuery(id, {
    enabled: Number.isFinite(id) && !isBare,
  })

  // The configurator brings its own full-height chrome and must not sit inside
  // the customer content column.
  if (isBare) {
    return (
      <ShadowUserProvider shadowUserId={id}>
        <LightThemeScope className="flex min-h-0 flex-1">
          <Outlet />
        </LightThemeScope>
      </ShadowUserProvider>
    )
  }

  const targetName = targetUser?.displayName || targetUser?.email || `#${id}`

  return (
    <ShadowUserProvider shadowUserId={id}>
      <CustomerShell
        banner={
          <Callout
            tone="warning"
            layout="banner"
            action={
              // eslint-disable-next-line no-restricted-syntax -- sits inside the banner and inherits its ink; a Button would bring its own
              <button
                type="button"
                onClick={() =>
                  void router.navigate({
                    to: asRoute(entryUrl.current ?? FALLBACK_EXIT),
                    replace: true,
                  })
                }
                className="flex flex-row items-center gap-1.5 rounded-full px-2 py-1 font-medium transition-colors hover:bg-current/10"
              >
                <ArrowLeft size={12} aria-hidden />
                {DASHBOARD.shadow.leave}
              </button>
            }
          >
            <span className="flex flex-row items-center gap-2 font-medium">
              <Eye size={14} aria-hidden />
              {DASHBOARD.shadow.viewingAs}{' '}
              <span className="font-semibold">{targetName}</span>
              {/* The configurator here writes the customer's own selections,
                  and `ProjectAccessGuard` refuses that for an advisor the
                  project does not name. Said in the banner rather than by
                  disabling: these are the customer's controls, and they read
                  no read-only scope. */}
              {watchingOnly ? (
                <span className="font-normal">
                  {PROJECTS.notAssigned.watchingOnly}
                </span>
              ) : null}
            </span>
          </Callout>
        }
      >
        <Outlet />
      </CustomerShell>
    </ShadowUserProvider>
  )
}

export const Route = createFileRoute('/internal/customer-view/$userId')({
  validateSearch: (search: Record<string, unknown>): ShadowSearch => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
  component: CustomerViewLayout,
})
